import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';

/**
 * Dark product-studio environment: near-black cyclorama + a few large softboxes.
 * The GLB is glossy (roughness ~0.17–0.30) so it mirrors its environment.
 */
function buildStudioEnv(): THREE.Scene {
  const env = new THREE.Scene();
  const shell = new THREE.Mesh(
    new THREE.SphereGeometry(10, 32, 16),
    new THREE.MeshBasicMaterial({ color: 0x0b0b0c, side: THREE.BackSide }),
  );
  env.add(shell);
  const box = (w: number, h: number, intensity: number, color: number, pos: [number, number, number]) => {
    const m = new THREE.Mesh(
      new THREE.PlaneGeometry(w, h),
      new THREE.MeshBasicMaterial({ color: new THREE.Color(color).multiplyScalar(intensity), side: THREE.DoubleSide }),
    );
    m.position.set(...pos);
    m.lookAt(0, 0, 0);
    env.add(m);
  };
  box(5, 3.2, 3.2, 0xfff3e6, [-4.5, 5, 4]);
  box(1.2, 7, 2.4, 0xe8f0ff, [6, 1, -3]);
  box(1.2, 7, 1.6, 0xeef2f6, [-6.5, 0.5, -2.5]);
  box(4, 2, 0.6, 0xdfe6ee, [4.5, 1.5, 5.5]);
  box(8, 8, 1.1, 0xf4f4f2, [0, 9, 0]);
  box(6, 2, 0.12, 0x6a5a50, [0, -5, 3]);
  return env;
}

// Unmodified northline-gate-valve-draco-webp.glb (1,384,808 bytes) hosted on CDN.
export const GATE_VALVE_GLB_URL = 'https://public.readdy.ai/ai/img_res/sandbox/9ef4ba4c795c54ca3c0511bf66073982/6749083a-fe7e-40dd-ae81-e222e056b134_northline-gate-valve.json';
const DRACO_DECODER_PATH = 'https://www.gstatic.com/draco/versioned/decoders/1.5.7/';

const FOV = 22;
const FRAME_HALF = 0.52; // world half-height mapped onto the framing rect
const DIST = FRAME_HALF / Math.tan(THREE.MathUtils.degToRad(FOV / 2));
const SLOT_RATIO = 1214 / 1295;

export type GateValveStatus = 'loading' | 'ready' | 'error';

export interface FrameRect { x: number; y: number; w: number; h: number }

/** Framing rects (canvas px) for the three scenes. r1 = measured hero plinth slot. */
export interface SceneFrames { r1: FrameRect; r2: FrameRect; r3: FrameRect }

interface GateValve3DProps {
  className?: string;
  progressRef: { current: number };
  getFrames: (w: number, h: number) => SceneFrames;
  /** Projected handwheel centre/radius in canvas px (exit offset excluded) — for the SVG handoff. */
  onHandwheel?: (x: number, y: number, r: number) => void;
  onStatus?: (s: GateValveStatus, info?: string) => void;
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const smooth = (t: number) => t * t * (3 - 2 * t);
export const seg = (p: number, a: number, b: number) => smooth(clamp01((p - a) / (b - a)));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const lerpRect = (a: FrameRect, b: FrameRect, t: number): FrameRect => ({
  x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t), w: lerp(a.w, b.w, t), h: lerp(a.h, b.h, t),
});

/**
 * Single scroll timeline (p = 0..1 over ~300vh):
 * 0–.06 hold S1 · .06–.36 orbit to side (S2) · .36–.46 hold · .46–.72 crane up to top-oblique (S3)
 * · .72–.80 hold · .80–.96 exit right + fade.
 * Camera orbits on spherical coords (azimuth/elevation) around a stable target; model never rotates.
 */
export function cameraAt(p: number) {
  const a = seg(p, 0.06, 0.36);
  const b = seg(p, 0.46, 0.72);
  const e = seg(p, 0.8, 0.96);
  return {
    a, b, e,
    // S1 ≈14° (more frontal) → S2 side ≈88° → S3 true top-down (el≈89.9°, axis-aligned azimuth 90°)
    az: THREE.MathUtils.degToRad(14 + (88 - 14) * a + (90 - 88) * b),
    el: THREE.MathUtils.degToRad(1 + 4 * a + (89.9 - 5) * b),
  };
}

export const slotRect = (cx: number, cy: number, h: number): FrameRect => ({
  x: cx - (h * SLOT_RATIO) / 2, y: cy - h / 2, w: h * SLOT_RATIO, h,
});

export default function GateValve3D({ className = '', progressRef, getFrames, onHandwheel, onStatus }: GateValve3DProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const cb = useRef({ getFrames, onHandwheel, onStatus, progressRef });
  cb.current = { getFrames, onHandwheel, onStatus, progressRef };

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    let disposed = false;
    const report = (s: GateValveStatus, info?: string) => { if (!disposed) cb.current.onStatus?.(s, info); };

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
    } catch (e) {
      report('error', 'WebGL unavailable');
      return undefined;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
    renderer.setClearColor(0x000000, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    Object.assign(renderer.domElement.style, { width: '100%', height: '100%', display: 'block' });
    host.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const pmrem = new THREE.PMREMGenerator(renderer);
    const envScene = buildStudioEnv();
    const envTex = pmrem.fromScene(envScene, 0.02).texture;
    envScene.traverse((o) => {
      const mm = o as THREE.Mesh;
      if (mm.geometry) mm.geometry.dispose();
      (mm.material as THREE.Material | undefined)?.dispose();
    });
    scene.environment = envTex;
    scene.environmentIntensity = 1.0;

    const camera = new THREE.PerspectiveCamera(FOV, 1, 0.05, 50);
    scene.add(camera);
    // Studio rig travels with the camera so the product keeps its approved lighting from every angle.
    // Local positions are offset by -DIST so light directions match the approved Scene 01 setup exactly.
    const rig: Array<[number, number, [number, number, number]]> = [
      [0xfff1e2, 1.25, [-2.2, 3, 2.6]],
      [0xdfe8f2, 0.22, [2.5, 0.8, 2]],
      [0xeef4ff, 1.1, [1.8, 2.2, -2.8]],
      [0xff6a45, 0.18, [3, -0.4, -1]],
    ];
    rig.forEach(([c, i, pos]) => {
      const l = new THREE.DirectionalLight(c, i);
      l.position.set(pos[0], pos[1], pos[2] - DIST);
      l.target.position.set(0, 0, -DIST);
      camera.add(l, l.target);
    });

    const pivot = new THREE.Group();
    scene.add(pivot);

    let W = 1; let H = 1;
    let frames: SceneFrames | null = null;
    let baseY = 0;
    const hw = new THREE.Vector3(0, 0.44, 0);
    let hwR = 0.16;
    let loaded = false;
    let last = -1;
    let dirty = true;
    const tmp = new THREE.Vector3();
    const right = new THREE.Vector3();
    const target = new THREE.Vector3();
    const upVec = new THREE.Vector3();
    const topUp = new THREE.Vector3();

    const setFrame = (r: FrameRect) => {
      camera.aspect = r.w / r.h;
      camera.setViewOffset(r.w, r.h, -r.x, -r.y, W, H);
      camera.updateProjectionMatrix();
    };

    const apply = (p: number) => {
      if (!frames) return;
      const c = cameraAt(p);
      // target: model centre (S1/S2) → handwheel centre (S3)
      target.set(hw.x * c.b, (baseY + hw.y) * c.b, hw.z * c.b);
      camera.position.set(
        target.x + DIST * Math.cos(c.el) * Math.sin(c.az),
        target.y + DIST * Math.sin(c.el),
        target.z + DIST * Math.cos(c.el) * Math.cos(c.az),
      );
      // Blend camera up from world-Y to the horizontal 'away' axis so the vertical top view never flips
      // and stays square to the screen.
      topUp.set(-Math.sin(c.az), 0, -Math.cos(c.az));
      upVec.set(0, 1, 0).lerp(topUp, c.b * c.b).normalize();
      camera.up.copy(upVec);
      camera.lookAt(target);
      camera.updateMatrixWorld();
      scene.environmentRotation.set(0, c.az, 0);
      const rect = lerpRect(lerpRect(frames.r1, frames.r2, c.a), frames.r3, c.b);
      // project handwheel at the settled S3 framing (before exit drift) for the SVG handoff
      setFrame(rect);
      tmp.set(hw.x, baseY + hw.y, hw.z).project(camera);
      const sx = ((tmp.x + 1) / 2) * W; const sy = ((1 - tmp.y) / 2) * H;
      right.setFromMatrixColumn(camera.matrixWorld, 0).multiplyScalar(hwR);
      tmp.set(hw.x, baseY + hw.y, hw.z).add(right).project(camera);
      const rx = ((tmp.x + 1) / 2) * W - sx;
      cb.current.onHandwheel?.(sx, sy, Math.abs(rx));
      if (c.e > 0) setFrame({ ...rect, x: rect.x + c.e * W * 0.34 });
      renderer.domElement.style.opacity = String(1 - c.e);
    };

    const render = () => { if (!disposed) renderer.render(scene, camera); };
    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (!loaded) return;
      const p = cb.current.progressRef.current;
      if (!dirty && Math.abs(p - last) < 0.00002) return;
      dirty = false;
      last = p;
      apply(p);
      render();
    };
    raf = requestAnimationFrame(tick);

    const resize = () => {
      W = host.clientWidth; H = host.clientHeight;
      if (!W || !H) return;
      renderer.setSize(W, H, false);
      frames = cb.current.getFrames(W, H);
      dirty = true;
    };
    const ro = new ResizeObserver(resize);
    ro.observe(host);
    resize();

    const draco = new DRACOLoader();
    draco.setDecoderPath(DRACO_DECODER_PATH);
    const loader = new GLTFLoader();
    loader.setDRACOLoader(draco);
    report('loading');
    loader.load(
      GATE_VALVE_GLB_URL,
      (gltf) => {
        if (disposed) return;
        const model = gltf.scene;
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        model.position.sub(center); // geometry untouched, only re-centred
        model.updateMatrixWorld(true);
        const s = 1 / size.y;
        pivot.scale.setScalar(s);
        // valve base sits exactly on the frame bottom (calibrated plinth line)
        baseY = -FRAME_HALF + 0.5;
        pivot.position.set(0, baseY, 0);

        // Measure the real handwheel from the top band of vertices (centre + radius, normalised units)
        // (rising stems poke above the wheel, so pick the widest horizontal slice in the top 25%)
        const SL = 24;
        const slices = Array.from({ length: SL }, () => new THREE.Box3());
        const v = new THREE.Vector3();
        const top = size.y * 0.5;
        const band = size.y * 0.25;
        model.traverse((o) => {
          const mesh = o as THREE.Mesh;
          const pos = mesh.geometry?.attributes?.position;
          if (!pos) return;
          for (let i = 0; i < pos.count; i += 1) {
            v.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
            const k = Math.floor(((top - v.y) / band) * SL);
            if (k >= 0 && k < SL) slices[k].expandByPoint(v);
          }
        });
        let best = -1; let bestW = 0;
        slices.forEach((b, k) => {
          if (b.isEmpty()) return;
          const bs = b.getSize(new THREE.Vector3());
          const wdt = Math.min(bs.x, bs.z); // wheel is round: both horizontal extents large
          if (wdt > bestW * 1.04) { bestW = wdt; best = k; }
        });
        if (best >= 0) {
          const hb = slices[best].clone();
          if (best > 0 && !slices[best - 1].isEmpty()) hb.union(slices[best - 1]);
          const hc = hb.getCenter(new THREE.Vector3());
          const hs = slices[best].getSize(new THREE.Vector3());
          hw.set(hc.x * s, hc.y * s, hc.z * s);
          hwR = (Math.max(hs.x, hs.z) / 2) * s;
        }

        const aniso = renderer.capabilities.getMaxAnisotropy();
        model.traverse((o) => {
          const m = (o as THREE.Mesh).material as THREE.MeshStandardMaterial | undefined;
          if (!m) return;
          [m.map, m.metalnessMap, m.roughnessMap, m.normalMap].forEach((t) => {
            if (t) { t.anisotropy = Math.min(8, aniso); t.needsUpdate = true; }
          });
        });
        pivot.add(model);
        loaded = true;
        dirty = true;
        report('ready', `handwheel r=${hwR.toFixed(3)} c=${hw.toArray().map((n) => n.toFixed(3)).join(',')}`);
      },
      undefined,
      (err) => {
        // eslint-disable-next-line no-console
        console.error('[GateValve3D] GLB/Draco load failed', err);
        report('error', String((err as Error)?.message || err));
      },
    );

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      ro.disconnect();
      draco.dispose();
      envTex.dispose();
      pmrem.dispose();
      scene.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        const mat = mesh.material as THREE.MeshStandardMaterial | undefined;
        if (mat) {
          [mat.map, mat.normalMap, mat.roughnessMap, mat.metalnessMap, mat.aoMap, mat.emissiveMap].forEach((t) => t?.dispose());
          mat.dispose();
        }
      });
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, []);

  return <div ref={hostRef} className={className} aria-hidden="true" />;
}